import React, { useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import {
  ParsedParquetData,
  isChunkedParsedData,
} from "../../utils/parquet";
import { CONTROL_LABEL_SX } from "../../utils/control_label";
import { ContainerTotals, countContainers } from "./container_totals";

/**
 * Header summary for Container Mapping: how many containers the current
 * (filtered) view recommends, total and per destination. Saves planners from
 * exporting the CSV and pivoting just to get the count.
 */
export const ContainerTotalsPanel = ({
  parsedData,
}: {
  parsedData?: ParsedParquetData | null;
}) => {
  const computed = useMemo(
    () =>
      parsedData && !isChunkedParsedData(parsedData)
        ? countContainers(parsedData.arrowTable)
        : null,
    [parsedData],
  );

  // parsedData goes null while a filter change refetches; keep showing the
  // last counts so the summary doesn't blink out on every interaction.
  const lastTotalsRef = useRef<ContainerTotals | null>(null);
  if (computed) lastTotalsRef.current = computed;
  const totals = computed ?? lastTotalsRef.current;

  if (!totals) return null;

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <Box sx={CONTROL_LABEL_SX}>Recommended containers</Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          columnGap: 2,
          rowGap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: (t) => t.palette.atomic.ink }}
        >
          {totals.total} {totals.total === 1 ? "container" : "containers"}
        </Typography>
        {totals.byLocation.map(({ location, count }) => (
          <Typography
            key={location}
            variant="body2"
            sx={{ color: (t) => t.palette.atomic.inkSecondary }}
          >
            {location || "Unknown location"}: {count}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
